import wsPlay from "@/js/wsPlay"
import http from "@/js/http"
import { openMsgToast } from "@/js/message"

// 定义接口类型
interface OssPlayConfig {
  msg: string
  rate?: number
  speed?: number
  audioPath?: string
  id?: symbol | null
}

interface TtsResponse {
  code: number
  message: string
  success: boolean
  tts?: string
}

class OssPlay {
  // 单例实例
  private static instance: OssPlay | null = null

  // 音频实体
  private audio: HTMLAudioElement | null = null
  // 消息
  private msg = ""
  // 速率（oss需要）
  private rate = 1.0
  // 访问oss固定路径
  private readonly urlHead = "https://resource.yingyutifen.cn/audio/"
  // 防盗链oss访问地址
  private readonly newUrlHead = "https://resource.yingyutifen.cn/a2504/"
  // 访问oss完整路径
  private url = ""
  // 加载状态
  public loading = false
  // 速度（科大讯飞需要）
  private speed = 60
  // oss音频库（拼接在url路径上）
  private audioPath = "DN001"
  // 当前音频播放进度百分比
  public progress = 0
  // 发音类型：1为AI发音，2为有道发音
  private type = 1
  // 进度回调
  private onProgress: ((val: number) => void) | null = null
  private progressRAF: number | null = null
  // 当前实例唯一标识符
  public instanceId: symbol | null = null

  // 私有构造函数，防止外部直接创建实例
  constructor() {
    // 初始化默认值
  }

  // 获取单例实例的静态方法
  public static getInstance(): OssPlay {
    if (!OssPlay.instance) {
      OssPlay.instance = new OssPlay()
    }
    return OssPlay.instance
  }

  // 初始化方法，替代原来的构造函数功能
  public init(config: OssPlayConfig): OssPlay {
    const { msg, rate = 1.0, speed = 50, audioPath = "DN001", id = null } = config

    // 如果当前有音频在播放，先停止它
    this.stop()

    this.instanceId = id
    this.msg = msg
    this.rate = rate
    this.speed = speed
    this.audioPath = audioPath
    this.progress = 0

    return this
  }

  // 设置进度回调
  public setProgressCallback(callback: (val: number) => void): void {
    this.onProgress = callback
  }

  // 获取当前进度
  public getProgress(): number {
    return this.progress
  }

  // 获取加载状态
  public isLoading(): boolean {
    return this.loading
  }

  // 停止当前播放并释放资源
  public stop(): void {
    if (this.audio) {
      this.audio.pause()
      this.audio = null
      this.loading = false
    }
    this.clearProgressAnimation()
  }

  // 清除进度动画
  private clearProgressAnimation(): void {
    if (this.progressRAF) {
      cancelAnimationFrame(this.progressRAF)
      this.progressRAF = null
    }
  }

  // 获取音频URL
  private async getUrl(msg: string): Promise<boolean> {
    try {
      const res = await http.post(
        "/tts/speak",
        { text: this.filterInitMsg(msg) },
        "",
        "",
        "",
        5000,
        true
      )

      if (res.data.code !== 9000) {
        await openMsgToast(res.data.message)
        return false
      }

      if (res.data.success && res.data.tts) {
        this.url = this.newUrlHead + res.data.tts
      } else {
        this.url = this.urlHead + this.audioPath + "/" + encodeURI(this.filterMsg(msg)) + ".mp3"
      }

      this.audio = new Audio(this.url)
      return true
    } catch (error) {
      console.error("获取音频URL失败:", error)
      return false
    }
  }

  // 更新播放进度
  private updateProgress = (): void => {
    if (!this.audio) return

    const currentTime = this.audio.currentTime
    const duration = this.audio.duration

    if (!isNaN(duration) && duration > 0) {
      this.progress = (currentTime / duration) * 100
      this.onProgress?.(this.progress)
    }

    if (!this.audio.paused && !this.audio.ended) {
      this.progressRAF = requestAnimationFrame(this.updateProgress)
    }
  }

  // 开始播放音频
  private async canPlay(): Promise<void> {
    if (!this.audio) return

    this.loading = true
    this.audio.playbackRate = this.rate

    try {
      await this.audio.play()
      this.progressRAF = requestAnimationFrame(this.updateProgress)

      const onEnded = (): void => {
        this.loading = false
        this.progress = 100
        this.onProgress?.(this.progress)
        this.clearProgressAnimation()
        this.audio?.removeEventListener("ended", onEnded)
      }

      this.audio.addEventListener("ended", onEnded)
    } catch (error) {
      console.error("播放音频失败:", error)
      this.loading = false
      this.clearProgressAnimation()
      openMsgToast("请先点击页面任意位置以允许音频播放", "middle", 3000)
    }
  }

  // 播放音频
  public async play(tts?: string): Promise<void> {
    // 如果已经在播放，不执行任何操作
    if (this.loading) {
      return
    }

    // play 前先 stop，确保没有残留 audio
    this.stop()

    // 事件监听函数具名，便于解绑
    const onCanPlay = async (): Promise<void> => {
      this.audio?.removeEventListener("canplay", onCanPlay)
      await this.canPlay()
    }

    const onError = (): void => {
      this.audio?.removeEventListener("error", onError)
      this.loading = true
      this.audio = null

      // 使用备用通道
      wsPlay.connectWebSocket(this.msg, undefined, undefined, this.speed)

      let fakeProgress = this.progress
      const timer = setInterval(() => {
        fakeProgress += 5
        if (fakeProgress >= 100) {
          fakeProgress = 100
          clearInterval(timer)
          this.loading = false
          this.onProgress?.(fakeProgress)
        } else {
          this.onProgress?.(fakeProgress)
        }
      }, 100)
    }

    try {
      if (tts) {
        this.url = this.newUrlHead + tts
        this.audio = new Audio(this.url)
      } else {
        const res = await this.getUrl(this.msg)
        if (!res) return
      }

      if (this.audio && !this.loading) {
        this.audio.addEventListener("canplay", onCanPlay)
        this.audio.addEventListener("error", onError)
      }
    } catch (error) {
      console.error("播放音频初始化失败:", error)
    }
  }

  // 暂停播放
  public pause(): void {
    if (this.audio) {
      this.audio.pause()
      this.loading = false
    }
  }

  // 重置状态
  public reset(): void {
    this.stop()
    this.progress = 0
    this.onProgress?.(this.progress)
  }

  // 过滤初始消息
  private filterInitMsg(msg: string): string {
    return msg.trim().replaceAll("#", " ")
  }

  // 过滤消息
  private filterMsg(msg: string): string {
    return msg.replace(/[\s;,/.；，。…()（）#]/g, "")
  }
}

// 导出获取实例的方法，而不是类本身
export default OssPlay.getInstance
